(ns quuppa.peweb.connection
  "Handles Sente connection, integration with re-frame and a component
  to display notice if connection is missing."
  (:require [re-frame.core :as rf]
            [re-frame.db :as rf-db]
            [ajax.core :as ajax]
            [komponentit.timeago :as timeago]
            [taoensso.sente :as sente]
            [taoensso.encore :as enc]
            [quuppa.sente.transit :refer [transit-packer]]
            [quuppa.utils :as utils]))

(defn- dispatch-result-event
  "Dispatch with re-frame. arg is conjoined to the event (event-or-cb-fn).
  If the event is a function, the function is called with arg as argument."
  [event-or-cb-fn arg]
  (if (fn? event-or-cb-fn)
    (event-or-cb-fn arg)
    (rf/dispatch (conj event-or-cb-fn arg))))

(defn send-event [event-or-map]
  ;; Should be okay to access app-db in fx handler?
  (let [f (-> @rf-db/app-db :ws :socket :send-fn)
        event (if (map? event-or-map)
                (:event event-or-map)
                event-or-map)
        {:keys [on-error on-success timeout]
         :or {timeout 15000}}
        (when (map? event-or-map)
          event-or-map)]
    (if (or on-error on-success)
      (f event timeout
         (fn [resp]
           (cond
             ;; Sente: Special messages  * Callback replies: :chsk/closed, :chsk/timeout, :chsk/error
             (sente/cb-error? resp)
             (do
               (js/console.error (utils/str-format "Sente error reply with event %s: %s" event resp))
               (when (= :chsk/timeout resp)
                 (js/console.warn ":chsk/timeout, did you forget to respond to event in backend? Specifying :on-error or :on-success expects reply from server side."))
               (when on-error
                 ;; wrap Sente keyword to "event-handler-error" format
                 (dispatch-result-event on-error {:type    :sente-error
                                                  :message "Error while waiting for response"
                                                  :err-msg "Error while waiting for response"
                                                  :ex-data {:error   resp
                                                            :event   event
                                                            :timeout timeout}})))

             (:event-handler-error resp)
             (if on-error
               (dispatch-result-event on-error (:event-handler-error resp))
               #_:clj-kondo/ignore
               (do
                 (js/console.warn ":event-handler-error received, but no :on-error handler present for event" (str (first event)))
                 ;; TODO dispatch 'default error' via `:notifications/notify`
                 ;; there is renderer for `:event-handler-error`
                 #_(rf/dispatch [:notifications/notify err])))

             on-success
             (when-not (= on-success :ignore)
               (dispatch-result-event on-success resp))

             :else
             (js/console.warn
               (utils/str-format
                 "Unexpected callback situation with event %s. No clause match, is this unexpected error case? Reply: %s"
                 event
                 resp)))))
      (f event))))

;;
;; Sente integration
;;

(def csrf-token
  (atom (when-let [el (.getElementById js/document "sente-csrf-token")]
          (.getAttribute el "data-csrf-token"))))

(defmulti event-msg-handler :id)

(defn ws-disconnect [ws]
  (when-let [stop-fn (-> ws :router)]
    (js/console.log "Stop sente event router")
    (stop-fn))
  (when-let [chsk (-> ws :socket :chsk)]
    (js/console.log "Disconnect chsk")
    (sente/chsk-disconnect! chsk)))

(rf/reg-event-fx
  ::initialize []
  (fn [{:keys [db]} [_ opts]]
    (ws-disconnect (:ws db))

    (when-not @csrf-token
      (js/console.warn "CSRF token NOT detected in HTML, default Sente config will reject requests"))

    (let [channel-socket
          (sente/make-channel-socket-client!
            (or (:path opts) "/chsk")
            @csrf-token
            {:type :auto
             :params {}
             :packer (transit-packer)
             ;; Set max retry timeout to 30 seconds
             :backoff-ms-fn #(enc/exp-backoff % {:max (* 30 1000)})})

          router (sente/start-client-chsk-router!
                   (:ch-recv channel-socket)
                   #(event-msg-handler (assoc % :opts opts)))]

      {:db (assoc db :ws {:opts opts
                          :socket channel-socket
                          :router router})})))

(rf/reg-event-fx
  ::reconnect
  (fn [{:keys [db]} _]
    {:fx [[:dispatch [::initialize (:opts (:ws db))]]]}))

(rf/reg-event-fx
  ::disconnect
  (fn [{:keys [db]} _]
    (ws-disconnect (:ws db))))

(rf/reg-event-fx
  ::bad-csrf-token
  (fn [_ [_ err]]
    (when (-> err :response :error (= "invalid-csrf"))
      (js/window.location.reload))))

(rf/reg-event-fx
  ::ok-csrf-token
  (fn [_ _]
    nil))

(rf/reg-event-fx
  ::test-csrf-token
  (fn [_ _]
    {:fx [[:http-xhrio {:method          :post
                        :uri             "/check-csrf"
                        :headers         {"x-csrf-token" @csrf-token}
                        :on-failure      [::bad-csrf-token]
                        :on-success      [::ok-csrf-token]
                        :format          (ajax/text-request-format)
                        :response-format (ajax/json-response-format {:keywords? true})}]]}))

(defmethod event-msg-handler :default [{:as ev-msg :keys [event]}]
  (js/console.warn "Unhandled event: " ev-msg event))

(defmethod event-msg-handler :chsk/state [{[old-state state] :?data, :keys [opts]}]
  (rf/dispatch [::set-state state])

  (cond
    (:first-open? state)
    (do
      (when-let [e (:on-open opts)]
        (rf/dispatch e))
      (js/console.log "Channel socket successfully established!"))

    (and (not (:open? old-state))
         (:open? state))
    (do
      (when-let [e (:on-reconnect opts)]
        (rf/dispatch e))
      (js/console.log "Channel socket successfully reconnected!"))

    ;; If the session has expired, ws connection cannot be re-opened, in which case, we need to login again.
    ;; See CloseEvent codes at https://developer.mozilla.org/en-US/docs/Web/API/CloseEvent
    ;; TODO: Websocket close reason doesn't tell the problem.
    ;; We'll need to do HTTP request to check the real problem reason:
    ;; 1. bad session/no csrf -> reload page
    ;; 1. no response or anything else -> wait
    (= 1006 (-> state :last-ws-close :code))
    (rf/dispatch [::test-csrf-token])

    :else
    (js/console.log "Channel socket state change:" state)))

(defmethod event-msg-handler :chsk/recv [{:keys [?data]}]
  (let [[ev-id ev-data] ?data
        event           (:event ev-data)]
    (case ev-id
      :chsk/ws-ping nil
      :qpe/event   (rf/dispatch [(first event) (:qpe-identity ev-data) (second event)])
      (rf/dispatch ?data))))


(defmethod event-msg-handler :chsk/handshake [_]
  (js/console.debug "Sente :chsk/handshake received"))

;;
;; Re-frame WS connection state
;;

(rf/reg-event-db
  ::set-state
  (fn event-connection-established?
    [db [_ value]]
    (assoc-in db [:ws :state] value)))

(rf/reg-sub
  ::state
  (fn sub-connection-state
    [db [_]]
    (get-in db [:ws :state])))

(rf/reg-sub
  ::connection-established?
  :<- [::state]
  (fn sub-connection-established?
    [state [_]]
    ;; Also consider pending connection established so the
    ;; warning doesn't flash when opening the app.
    (or (nil? state) ;; pending connection
        (:open? state))))

;;
;; Connection lost dialog
;;

(defn format-seconds [{:keys [duration-type duration-value]}]
  (str (case duration-type
         :ss (str duration-value " seconds")
         nil)))

(defn connection-status []
  (let [open? @(rf/subscribe [::connection-established?])]
    (when-not open?
      (let [{:keys [udt-next-reconnect]} @(rf/subscribe [::state])]
        [:div
         {:style {:position "fixed"
                  :top 0
                  :bottom 0
                  :left 0
                  :right 0
                  :background-color "rgba(0, 0, 0, 0.2)"
                  :z-index 2000}}
         [:div
          {:style {:position "absolute"
                   :top "50%"
                   :right "50%"
                   :width "500px"
                   :height "200px"
                   :box-sizing "border-box"
                   :margin-right "-250px"
                   :margin-top "-100px"
                   ; :font-size "3rem"
                   :background-color "darkred"
                   :color "#fff"
                   :padding "2rem"
                   :display "flex"
                   :flex-direction "column"
                   :align-items "center"
                   :justify-content "center"}}
          [:h1 "Disconnected!"]
          [:h4 "Reconnecting in "
           [timeago/timeago-2
            {:value (js/Date. udt-next-reconnect)
             :format-fn format-seconds}]]

          [:button.btn.btn-light
           {:on-click #(rf/dispatch [::reconnect])}
           "Reconnect now"]]]))))
